import { useState, useEffect, useCallback } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'
import { useHistoricalDataUpload } from '@/hooks/useHistoricalData'
import { useProductListItems, useProductList } from '@/hooks/useProductLists'
import { usePlanVersionLines } from '@/hooks/usePlanVersionLines'
import { useHierarchyFromPlan } from '@/hooks/useHierarchy'
import { ImportResultsSummary } from '@/components/PlanDetail/ImportResultsSummary'
import { PlanSearcher } from './PlanSearcher'
import { PlanPreview } from './PlanPreview'
import { FileUploadDropzone } from './FileUploadDropzone'
import { StepHeader } from './StepHeader'
import { ProcessingOverlay } from '@/components/common/ProcessingOverlay'
import { toast } from '@/stores/useToastStore'
import { downloadTemplate } from '@/utils/downloadTemplate'
import type { SearchablePlan } from '@/api/planSearch'

export function HistoricalDataSourceStep() {
  const {
    historicalDataSourceType,
    uploadedFileName,
    showHistoricalDataReview,
    historicalSourcePlan,
    productListId,
    setHistoricalDataSourceType,
    setUploadedFile,
    setHistoricalDataItems,
    setHistoricalSourcePlan,
    setProductListId,
    setProcessingStatus,
    setHasBlockingMismatches,
    resetHistoricalDataSource,
  } = usePlanWizardStore()

  // Local state for pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(5)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  // Upload hook (handles file upload + product list creation + SSE)
  const uploadHook = useHistoricalDataUpload()

  // Query for product list items (only after processing completes)
  const { data: productListData, isLoading: isLoadingProductItems } =
    useProductListItems(
      uploadHook.isCompleted ? productListId : null,
      page,
      pageSize
    )

  // Full product list record (valid/invalid/skipped counts) for the results summary
  const { data: productList } = useProductList(
    uploadHook.isCompleted ? productListId : null
  )

  const handleMismatchStatusChange = useCallback(
    (status: { hasMismatches: boolean; isLoading: boolean }) => {
      setHasBlockingMismatches(status.hasMismatches)
    },
    [setHasBlockingMismatches]
  )

  // Query for plan-based historical data (only when source type is plan)
  const { data: planHistoricalData } = usePlanVersionLines(
    historicalDataSourceType === 'plan'
      ? (historicalSourcePlan?.id ?? null)
      : null,
    page,
    pageSize
  )

  // Auto-populate hierarchy when source plan is selected
  useHierarchyFromPlan(
    historicalDataSourceType === 'plan'
      ? historicalSourcePlan?.hierarchyId
      : undefined
  )

  // Sync processing status to store when upload hook state changes
  useEffect(() => {
    if (uploadHook.status !== 'idle') {
      setProcessingStatus(uploadHook.status, uploadHook.progress)
    }
  }, [uploadHook.status, uploadHook.progress, setProcessingStatus])

  // Update store with product list data when it loads
  useEffect(() => {
    if (productListData && uploadHook.isCompleted) {
      setHistoricalDataItems(productListData.items, productListData.totalCount)
    }
  }, [productListData, uploadHook.isCompleted, setHistoricalDataItems])

  // Update store with plan data when it loads
  useEffect(() => {
    if (historicalDataSourceType === 'plan' && planHistoricalData) {
      setHistoricalDataItems(
        planHistoricalData.items,
        planHistoricalData.totalCount
      )
    }
  }, [historicalDataSourceType, planHistoricalData, setHistoricalDataItems])

  // Handle plan selection
  const handleSelectPlan = (plan: SearchablePlan) => {
    setHistoricalDataSourceType('plan')
    setHistoricalSourcePlan(plan)
    setHistoricalDataItems([], 0)
    setPage(1)
  }

  // Handle file selection
  const handleFileSelect = async (file: File) => {
    setPendingFile(file)
    setHistoricalDataSourceType('file')
    setUploadedFile(file.name)

    try {
      const result = await uploadHook.upload(file)
      setProductListId(result.productListId)
      // Items will be fetched via useProductListItems once processing completes
    } catch {
      toast.error('Failed to upload file. Please try again.')
    }
  }

  // Handle retry
  const handleRetry = async () => {
    if (pendingFile) {
      try {
        const result = await uploadHook.upload(pendingFile)
        setProductListId(result.productListId)
      } catch {
        toast.error('Failed to upload file. Please try again.')
      }
    }
  }

  // Handle change source (go back to selection mode)
  const handleChangeSource = () => {
    resetHistoricalDataSource()
    setPage(1)
    uploadHook.reset()
    setPendingFile(null)
  }

  // Determine what to show
  const isProcessing = uploadHook.isPending
  const isComplete = uploadHook.isCompleted
  const isError = uploadHook.isError

  // Show review mode when we have completed data
  const shouldShowReview =
    showHistoricalDataReview ||
    (historicalDataSourceType === 'plan' && historicalSourcePlan) ||
    (historicalDataSourceType === 'file' && isComplete)

  // Show processing overlay during upload/processing
  if (historicalDataSourceType === 'file' && (isProcessing || isError)) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <StepHeader
          title="Select Data Source"
          description={
            isError
              ? 'There was an error processing your file'
              : 'Your file is being processed'
          }
        />
        <ProcessingOverlay
          status={uploadHook.status}
          progress={uploadHook.progress}
          fileName={uploadedFileName}
          error={uploadHook.error}
          onRetry={handleRetry}
        />
      </div>
    )
  }

  // Plan source review - use shared PlanPreview component
  if (
    shouldShowReview &&
    historicalDataSourceType === 'plan' &&
    historicalSourcePlan
  ) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <StepHeader
          title="Select Data Source"
          description="Review your imported data below"
        />
        <PlanPreview
          plan={historicalSourcePlan}
          onChangeSource={handleChangeSource}
          tableTitle="Imported Data"
          itemCountLabel="items imported"
        />
      </div>
    )
  }

  // File source review - show after processing completes
  if (shouldShowReview && historicalDataSourceType === 'file') {
    return (
      <div className="flex flex-col gap-6 w-full">
        {/* Header */}
        <StepHeader
          title="Select Data Source"
          description="Review your imported data below"
        />

        {productList ? (
          <ImportResultsSummary
            productList={productList}
            validPagination={{
              items: productListData?.items ?? [],
              totalCount: productListData?.totalCount ?? 0,
              page,
              pageSize,
              totalPages: productListData?.totalPages ?? 1,
              isLoading: isLoadingProductItems,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
            onMismatchStatusChange={handleMismatchStatusChange}
          />
        ) : (
          <div className="flex items-center gap-2 text-[13px] text-[#556374]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading results…
          </div>
        )}
      </div>
    )
  }

  // Initial selection mode
  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Header */}
      <StepHeader
        title="Select Data Source"
        description="Import your data from an existing plan or upload a file to get started"
        className="mb-0"
      />

      {/* Plan Search Card */}
      <div className="bg-white p-6 rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)]">
        <h3 className="text-[16px] font-semibold text-[#1C1C1C] mb-4">
          Select Plan
        </h3>
        <PlanSearcher
          onSelect={handleSelectPlan}
          placeholder="Search plans by name"
        />
        <p className="text-[13px] text-[#556374] mt-3">
          Import historical data from an existing plan. Results will filter
          based on your selected plan type.
        </p>
      </div>

      {/* OR Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-[#E5E7EB]" />
        <span className="text-[14px] font-medium text-[#9CA3AF]">OR</span>
        <div className="flex-1 h-px bg-[#E5E7EB]" />
      </div>

      {/* File Upload Card */}
      <div className="bg-white p-6 rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)]">
        <h3 className="text-[16px] font-semibold text-[#1C1C1C] mb-4">
          Import Data File
        </h3>
        <FileUploadDropzone
          onFileSelect={handleFileSelect}
          isLoading={false}
          error={null}
        />
        <p className="text-[13px] text-[#556374] mt-3">
          <button
            onClick={() => {
              downloadTemplate().catch(() => {
                toast.error('Failed to download template')
              })
            }}
            className="text-[#CF1F2E] hover:underline cursor-pointer inline-flex items-center gap-1"
          >
            <Download className="h-3 w-3" />
            Download template
          </button>
        </p>
      </div>
    </div>
  )
}
